import { useState, useEffect, useRef } from 'react';
import { RevoltInput, RevoltGlassButton, RevoltColorPicker } from '../../../components';
import { SaveErrorModal } from './SaveErrorModal';
import './ConfigurationCard.css';

function normalizeTag(str) {
  return String(str || '').trim().toLowerCase().replace(/\s+/g, '-');
}

export function ConfigurationCard({
  app,
  canEdit,
  entryFilePath,
  filePickerSaveError,
  onClearFilePickerSaveError,
  onOpenFilePicker,
  onSaveEntryFilePath,
  onSaveTags,
  onSaveAppColor
}) {
  const [isSaving, setIsSaving] = useState(false);
  const [localEntryFilePath, setLocalEntryFilePath] = useState(entryFilePath || '');
  const [appColor, setAppColor] = useState(app?.settings?.appColor || null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorContext, setErrorContext] = useState(null); // 'entryPath' | 'color' | 'tags'
  const [localTags, setLocalTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const saveTimeoutRef = useRef(null);

  const MAX_TAGS = 20;

  // Update local state when prop changes
  useEffect(() => {
    setLocalEntryFilePath(entryFilePath || '');
  }, [entryFilePath]);

  // Show error modal when FilePickerModal save fails (parent-injected error)
  // Parent passes stable useCallback; we intentionally run only when filePickerSaveError changes
  useEffect(() => {
    if (filePickerSaveError) {
      setErrorMessage(filePickerSaveError);
      setErrorContext('entryPath');
      setShowErrorModal(true);
      onClearFilePickerSaveError?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onClearFilePickerSaveError is stable (parent useCallback)
  }, [filePickerSaveError]);

  // Update app color when app changes
  useEffect(() => {
    setAppColor(app?.settings?.appColor || null);
  }, [app]);

  // Update local tags when app changes
  useEffect(() => {
    setLocalTags(Array.isArray(app?.tags) ? [...app.tags] : []);
  }, [app?.tags]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  if (!app) return null;

  const appTypeNorm = String(app.appType || '')
    .toLowerCase()
    .trim();
  const isLocalApp = appTypeNorm === 'local';
  const isBrowserApp = appTypeNorm === 'browser';
  // Entry file path: preview/iframe entry — not used for on-disk local projects
  const showEntryFilePathSection = !isLocalApp;
  // Tags: discovery/sharing; only browser apps are shareable in product today
  const showTagsSection = isBrowserApp;

  const handleInputChange = (value) => {
    setLocalEntryFilePath(value);
  };

  const saveEntryPathIfChanged = async () => {
    if (!canEdit || isSaving || !onSaveEntryFilePath) return;
    if (localEntryFilePath === (entryFilePath || '')) return;
    setIsSaving(true);
    setShowErrorModal(false);
    setErrorMessage('');
    setErrorContext('entryPath');
    const timeoutId = setTimeout(() => {
      setIsSaving(false);
      setErrorMessage('Save operation timed out. Please try again.');
      setShowErrorModal(true);
    }, 10000);
    saveTimeoutRef.current = timeoutId;
    try {
      await onSaveEntryFilePath(localEntryFilePath);
      clearTimeout(timeoutId);
    } catch (error) {
      clearTimeout(timeoutId);
      setErrorMessage(error?.message || 'Failed to save entry file path.');
      setShowErrorModal(true);
    } finally {
      setIsSaving(false);
      saveTimeoutRef.current = null;
    }
  };

  const saveAppColor = async (color) => {
    if (!canEdit || isSaving || !onSaveAppColor) return;
    setIsSaving(true);
    setShowErrorModal(false);
    setErrorMessage('');
    setErrorContext('color');
    try {
      await onSaveAppColor(color);
    } catch (error) {
      setErrorMessage(error?.message || 'Failed to save app color.');
      setShowErrorModal(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEntryPathBlur = () => {
    saveEntryPathIfChanged();
  };

  const handleEntryPathKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveEntryPathIfChanged();
    }
  };

  const saveTags = async (tags) => {
    if (!canEdit || isSaving || !onSaveTags) return;
    setIsSaving(true);
    setShowErrorModal(false);
    setErrorMessage('');
    setErrorContext('tags');
    try {
      await onSaveTags(tags);
    } catch (error) {
      setErrorMessage(error?.message || 'Failed to save tags.');
      setShowErrorModal(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddTag = () => {
    const normalized = normalizeTag(tagInput);
    if (!normalized || localTags.includes(normalized)) {
      setTagInput('');
      return;
    }
    const next = [...localTags, normalized].slice(0, MAX_TAGS);
    setLocalTags(next);
    setTagInput('');
  };

  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setLocalTags(localTags.filter((t) => t !== tagToRemove));
  };

  const tagsHaveChanges = () => {
    const saved = [...(app?.tags || [])].sort();
    const local = [...localTags].sort();
    return JSON.stringify(saved) !== JSON.stringify(local);
  };

  const handleRetry = () => {
    setShowErrorModal(false);
    if (errorContext === 'entryPath') {
      saveEntryPathIfChanged();
    } else if (errorContext === 'color') {
      saveAppColor(appColor);
    } else if (errorContext === 'tags') {
      saveTags(localTags);
    }
  };

  return (
    <div className="account-card settings-card configuration-card">
      <div className="card-header configuration-header">
        <h2 className="card-title">
          <i className="fa-solid fa-sliders"></i> Configuration
        </h2>
      </div>
      <div className="card-body configuration-body">
        <div className="configuration-content">
          {/* 1) App color — all app types */}
          <div className="configuration-setting">
            <h3 className="configuration-setting-title">App Color</h3>
            <p className="configuration-setting-description">
              Choose a custom color for this app. This color may be used in various UI elements throughout the application.
            </p>
            <div className="configuration-input-row">
              <RevoltColorPicker
                value={appColor}
                onChange={(color) => {
                  setAppColor(color);
                  saveAppColor(color);
                }}
                disabled={!canEdit || isSaving}
              />
              <div className="color-status">
                {appColor ? (
                  <span className="color-value">{appColor}</span>
                ) : (
                  <span className="color-none">No color set</span>
                )}
              </div>
              {canEdit && appColor && (
                <RevoltGlassButton
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setAppColor(null);
                    saveAppColor(null);
                  }}
                  title="Remove color"
                  disabled={isSaving}
                >
                  <i className="fa-solid fa-xmark"></i>
                </RevoltGlassButton>
              )}
            </div>
          </div>

          {showEntryFilePathSection && (
            <>
              <div className="configuration-divider" />
              <div className="configuration-setting">
                <h3 className="configuration-setting-title">Entry File Path</h3>
                <p className="configuration-setting-description">
                  The HTML file that serves as the entry point for this application.
                </p>
                <div className="configuration-input-row">
                  <RevoltInput
                    value={localEntryFilePath}
                    onChange={(e) => handleInputChange(e.target.value)}
                    onBlur={handleEntryPathBlur}
                    onKeyDown={handleEntryPathKeyDown}
                    placeholder="e.g., index.html, application/index.html"
                    disabled={!canEdit || isSaving}
                    style={{ flex: 1 }}
                  />
                  {canEdit && (
                    <RevoltGlassButton
                      variant="ghost"
                      size="sm"
                      onClick={onOpenFilePicker}
                      style={{ marginLeft: 8 }}
                    >
                      <i className="fa-solid fa-folder-open"></i> Browse
                    </RevoltGlassButton>
                  )}
                </div>
              </div>
            </>
          )}

          {showTagsSection && (
            <>
              <div className="configuration-divider" />
              <div className="configuration-setting">
                <div className="configuration-setting-header-row">
                  <h3 className="configuration-setting-title">Tags</h3>
                  {canEdit && (
                    <RevoltGlassButton
                      variant="accent"
                      size="sm"
                      onClick={() => saveTags(localTags)}
                      disabled={!tagsHaveChanges() || isSaving}
                      title="Save tags"
                    >
                      Save
                    </RevoltGlassButton>
                  )}
                </div>
                <p className="configuration-setting-description">
                  Tags help users discover your app when searching on the community page. Add up to {MAX_TAGS} tags for easier searchability. Spaces are converted to dashes.
                </p>
                <div className="configuration-tags-row">
                  <RevoltInput
                    type="text"
                    placeholder="Add tags"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleTagKeyDown}
                    disabled={!canEdit || isSaving}
                    className="configuration-tag-input"
                  />
                  <div className="configuration-tags">
                    {localTags.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        className="configuration-tag"
                        onClick={() => handleRemoveTag(tag)}
                        disabled={!canEdit || isSaving}
                        aria-label={`Remove ${tag} tag`}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <SaveErrorModal
        isOpen={showErrorModal}
        onClose={() => {
          setShowErrorModal(false);
          onClearFilePickerSaveError?.();
        }}
        errorMessage={errorMessage}
        onRetry={handleRetry}
      />
    </div>
  );
}
